import { useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import useUserAccount from '../../../../hooks/userAccountHook';
import Button from '../../../../components/form-control/Button';
import useBilling from '../../../../hooks/billingHook';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../../../components/Loader';
import SubscriptionPlan from '../../../../models/SubscriptionPlan';
import GTable from '../../../../components/elements/GTable';
import { removeUnderscoresAndCapitalize } from '../../../../utils/miscFunctions';
import SubscriptionPlanDetails from './SubscriptionPlanDetails';
import { useNavigate } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import { CheckCircleIcon, EllipsisVerticalIcon } from 'lucide-react';
import PopOnMe from '../../../../components/elements/poponme';
import { useSelector } from 'react-redux';
import { getCompanyOnboardingStatusFromState } from '../../../../states/slices/globalSlice';

const Billing = () => {
  const { BillingModal, fetchSubscriptionPlans, companySubscribeToPlan, afterSuccessfullySubscribedModal, isSubscriptionSuccessful } = useBilling()
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showPlanDetails, setShowPlanDetails] = useState<boolean>(false);
  const navigate = useNavigate()
  const { getDashboardBase } = useRoute()
  const { user } = useUserAccount();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => fetchSubscriptionPlans(),
  })

  const getTableRowActions = (plan: SubscriptionPlan) => {
    const actions = [];

    actions.push({
      label: 'Edit',
      callback: () => navigate(`${getDashboardBase()}/settings/billing/subscription-plan?id=${plan.id}`),
    })

    return actions;
  };

  useQuery({
    queryKey: ['afterSuccessfullySubscribedModal', isSubscriptionSuccessful],
    queryFn: afterSuccessfullySubscribedModal,
    enabled: isSubscriptionSuccessful
  })

  const onRowClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowPlanDetails(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Plan',
        id: 'plan',
        columns: [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: ({ cell }: any) => {
              return (
                <span className='text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer' onClick={() => onRowClick(cell.row.original)}>{cell.value}</span>
              )
            }
          },
          {
            Header: 'Company Type',
            accessor: 'companyType',
            Cell: ({ cell }: any) => removeUnderscoresAndCapitalize(cell.value)
          },
          {
            Header: 'Plan Code',
            accessor: 'planCode',
            Cell: ({ cell }: any) => cell.value
          },
          {
            Header: 'Plan Tier',
            accessor: 'planTier',
            Cell: ({ cell }: any) => cell.value
          },
          {
            Header: 'Final Price',
            accessor: 'finalPrice',
            Cell: ({ cell }: any) => cell.value
          },
          {
            Header: 'Billing Cycle',
            accessor: 'billingCycle',
            Cell: ({ cell }: any) => cell.value
          },
          {
            Header: 'Custom Features',
            accessor: 'customFeatures',
            Cell: ({ cell }: any) => cell.value
          },
          {
            Header: "Action",
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  {getTableRowActions(row.row.original).length > 0 && (
                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                  )}
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );


  return (
    <div className="">

      {isLoading ? <Loader brollyLoaderFixed /> :
        plans?.length > 0 ?
          <PricingPlans plans={plans} columns={columns} companySubscribeToPlan={companySubscribeToPlan} />
          :
          <div className='flex flex-col items-center justify-center'>
            <h3 className='text-gray-500 dark:text-darkBg-subText text-xl font-semibold'>No Billing Found</h3>
          </div>
      }

      {selectedPlan && showPlanDetails && (
        <SubscriptionPlanDetails
          isOpen={showPlanDetails}
          onClose={() => setShowPlanDetails(false)}
          plan={selectedPlan}
        />
      )}
      <BillingModal />
    </div>
  )
};

export default Billing;


const PricingPlans = ({ plans, columns, companySubscribeToPlan }: { plans: SubscriptionPlan[], columns: any, companySubscribeToPlan: (planId: string) => void }) => {
  const { user } = useUserAccount()
  const onbordingPlan = useSelector(getCompanyOnboardingStatusFromState);
  const subscriptionPlanId = onbordingPlan ? onbordingPlan?.companyInfo?.subscriptionPlanId : undefined;

  return (
    <>
      {user?.isInternalAdmin() || user?.isInternalOwner() ?
        <GTable columns={columns} data={plans} showSearch={false} />
        :
        <>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-darkBg-mainText sm:text-4xl">Billing Plan Prices</h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-darkBg-subText">Choose the right plan for your business</p>
          </div>

          <div className="flex flex-col md:flex-row flex-wrap gap-8 justify-center">
            {plans.map((plan) => {
              const isCurrentPlan = subscriptionPlanId && subscriptionPlanId === plan.id;

              return (
                <div
                  key={plan.planCode}
                  className="flex min-w-[300px] flex-col rounded-2xl border border-gray-200 dark:border-darkBg-border bg-white dark:bg-darkBg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-6 ">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">
                      {plan.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-darkBg-subText">
                      {plan.planTier}
                    </p>

                    <div className="mt-4 flex items-baseline space-x-1">
                      <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-darkBg-mainText">
                        {plan.currency}{plan.finalPrice}
                      </span>
                      <span className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">
                        /{plan.billingCycle}
                      </span>
                    </div>

                    <div className="mt-6">
                      {isCurrentPlan ? (
                        <button
                          type="button"
                          className="w-full rounded-md border border-gray-300 dark:border-darkBg-border bg-white dark:bg-darkBg py-2.5 text-sm font-semibold text-gray-900 dark:text-darkBg-mainText shadow-sm"
                          disabled
                        >
                          Your current plan
                        </button>
                      ) : (
                        <Button
                          className="mt-0 w-full bg-primary-main hover:bg-primary-main text-white font-semibold py-2.5 px-4 rounded-md transition-colors"
                          label="Upgrade now"
                          onClick={() => companySubscribeToPlan(String(plan.id))}
                        />
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 px-6 py-5 rounded-b-2xl space-y-5">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">
                        Capabilities
                      </h4>
                      <ul className="space-y-2">
                        {[{
                          label: 'Free trial',
                          value: plan.hasTrial
                        }, {
                          label: '24/7 support',
                          value: plan.has24x7Support
                        }, {
                          label: 'Priority support',
                          value: plan.hasPrioritySupport
                        }, {
                          label: 'Dedicated account manager',
                          value: plan.hasAccountManager
                        }, {
                          label: 'Analytics',
                          value: plan.hasAnalytics
                        }, {
                          label: 'Advanced reports',
                          value: plan.hasAdvancedReports
                        }, {
                          label: 'API access',
                          value: plan.hasApiAccess
                        }, {
                          label: 'Custom integrations',
                          value: plan.hasCustomIntegrations
                        }, {
                          label: 'White-label',
                          value: plan.hasWhiteLabel
                        }].map((item) => (
                          <li key={item.label} className="flex items-center justify-between text-xs text-gray-700 dark:text-darkBg-subText">
                            <span>{item.label}</span>
                            <span
                            >
                              {item.value ?
                                <CheckCircleIcon className='text-green-700 h-4 w-4' />
                                :
                                <CheckCircleIcon className='text-gray-300 h-4 w-4' />
                              }
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">
                        Usage limits
                      </h4>
                      <ul className="space-y-2 text-xs text-gray-700 dark:text-darkBg-subText">
                        <li className="flex items-center justify-between">
                          <span>Users</span>
                          <span className="font-semibold">{plan.maxUsers}</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>Branches</span>
                          <span className="font-semibold">{plan.maxBranches}</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>Motor policies</span>
                          <span className="font-semibold">{plan.maxMotorPolicies}</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>Non-motor policies</span>
                          <span className="font-semibold">{plan.maxNonMotorPolicies}</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>Storage (GB)</span>
                          <span className="font-semibold">{plan.maxStorageGb}</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>API calls / month</span>
                          <span className="font-semibold">{plan.maxApiCalls}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      }
    </>
  )
}